import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { NOTIF_POLL_INTERVAL_MS } from '../../config/appConfig';
import { getNoLeidas, marcarLeida, marcarTodasLeidas } from '../../api/notificaciones';
import swal from '../../utils/swal';

/**
 * Hace polling de las notificaciones no leídas cada NOTIF_POLL_INTERVAL_MS.
 * Devuelve la lista, el contador y la acción para marcar una como leída.
 */
export function useNotificaciones() {
  const { token } = useAuth();
  const [noLeidas, setNoLeidas] = useState([]);
  // Evita pisar el estado si el componente se desmonta durante un fetch en curso.
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    return () => { montado.current = false; };
  }, []);

  // Fetch silencioso: si falla un tick de polling no molestamos al usuario.
  const fetchNoLeidas = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getNoLeidas();
      if (montado.current) setNoLeidas(Array.isArray(data) ? data : []);
    } catch (_) {
      // No bloqueante: un error de polling no debe interrumpir al usuario.
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setNoLeidas([]);
      return undefined;
    }
    fetchNoLeidas();
    const intervalo = setInterval(fetchNoLeidas, NOTIF_POLL_INTERVAL_MS);
    return () => clearInterval(intervalo);
  }, [token, fetchNoLeidas]);

  // Acción explícita del usuario: acá sí mostramos el error con swal.
  const marcar = useCallback(async (id) => {
    try {
      await marcarLeida(id);
      if (montado.current) {
        setNoLeidas(prev => prev.filter(n => n._id !== id));
      }
    } catch (_) {
      await swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo marcar la notificación como leída.',
        confirmButtonColor: '#db1414',
      });
    }
  }, []);

  // Marca todas como leídas de una vez (acción explícita del usuario).
  const marcarTodas = useCallback(async () => {
    try {
      await marcarTodasLeidas();
      if (montado.current) setNoLeidas([]);
    } catch (_) {
      await swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron marcar las notificaciones como leídas.',
        confirmButtonColor: '#db1414',
      });
    }
  }, []);

  return {
    noLeidas,
    count: noLeidas.length,
    refetch: fetchNoLeidas,
    marcar,
    marcarTodas,
  };
}
